/**
 * SkillEditModal - Edit skill properties
 */

import { useEffect, useState } from "react";
import type { Skill } from "@/types/story.ts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { useTranslation } from "react-i18next";

interface SkillEditModalProps {
  open: boolean;
  onClose: () => void;
  skill: Skill;
  onSave: (skill: Skill) => void;
}

export function SkillEditModal({ open, onClose, skill, onSave }: SkillEditModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Skill>(skill);
  const canSave = formData.name.trim().length > 0;

  useEffect(() => {
    setFormData(skill);
  }, [skill]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("modals.skill.title")}</DialogTitle>
          <DialogDescription>
            {t("modals.skill.description", { defaultValue: "Edit skill details" })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t("modals.skill.name")}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t("modals.skill.namePlaceholder", {
                defaultValue: "e.g., Sword Fighting",
              })}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="emoji">{t("modals.skill.emoji")}</Label>
            <Input
              id="emoji"
              value={formData.emoji}
              onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
              placeholder="⚔️"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="application">
              {t("modals.skill.application", { defaultValue: "Application" })}
            </Label>
            <Textarea
              id="application"
              value={formData.application || ""}
              onChange={(e) => setFormData({ ...formData, application: e.target.value })}
              placeholder={t("modals.skill.applicationPlaceholder", {
                defaultValue: "e.g., Duel pirates or battle sea monsters",
              })}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("modals.skill.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>{t("modals.skill.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
